import AssignmentRoundedIcon from "@mui/icons-material/AssignmentRounded";
import ExploreRoundedIcon from "@mui/icons-material/ExploreRounded";
import keys from "locale/keys";
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useSearchParams } from "react-router-dom";

import MoreSurveysContainer from "../moresurveys";
import {
  SurveysHubTabBar,
  SurveysHubTabButton,
  SurveysHubTabLabel,
  SurveysHubTabPanel,
  SurveysHubTabText,
  SurveysHubTabTrack,
  SurveysHubWrapper,
} from "./styled.components";
import SurveysContainer from "./SurveysContainer";
import {
  DEFAULT_SURVEYS_HUB_TAB,
  getSurveysHubTab,
  SURVEYS_HUB_TAB_PARAM,
  SURVEYS_HUB_TABS,
  SurveysHubTabId,
} from "./tabs";

const SurveysHubContainer = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const activeTab = getSurveysHubTab(location.search, location.state);

  const tabDetails: Record<
    SurveysHubTabId,
    { label: string; icon: ReactNode; content: ReactNode }
  > = {
    [SurveysHubTabId.MORE_SURVEYS]: {
      label: t(keys.survey_wall_1),
      icon: <ExploreRoundedIcon />,
      content: <MoreSurveysContainer embedded />,
    },
    [SurveysHubTabId.MY_SURVEYS]: {
      label: t(keys.survey_wall_2),
      icon: <AssignmentRoundedIcon />,
      content: <SurveysContainer />,
    },
  };

  const onTabChanged = (tabId: SurveysHubTabId) => {
    if (tabId === activeTab.id) return;
    const nextParams = new URLSearchParams(searchParams);
    if (tabId === DEFAULT_SURVEYS_HUB_TAB) {
      nextParams.delete(SURVEYS_HUB_TAB_PARAM);
    } else {
      nextParams.set(SURVEYS_HUB_TAB_PARAM, tabId);
    }
    setSearchParams(nextParams, { replace: true });
  };

  return (
    <SurveysHubWrapper fullBleed={activeTab.fullBleed}>
      <SurveysHubTabBar>
        <SurveysHubTabTrack role="tablist">
          {SURVEYS_HUB_TABS.map((tab) => {
            const isActive = activeTab.id === tab.id;
            return (
              <SurveysHubTabButton
                key={tab.id}
                type="button"
                role="tab"
                id={`surveys-hub-tab-${tab.id}`}
                aria-selected={isActive}
                aria-controls={`surveys-hub-panel-${tab.id}`}
                isActive={isActive}
                onClick={() => onTabChanged(tab.id)}
              >
                {tabDetails[tab.id].icon}
                <SurveysHubTabText>
                  <SurveysHubTabLabel>
                    {tabDetails[tab.id].label}
                  </SurveysHubTabLabel>
                </SurveysHubTabText>
              </SurveysHubTabButton>
            );
          })}
        </SurveysHubTabTrack>
      </SurveysHubTabBar>
      {/* Only the active tab is mounted, so inactive tabs don't keep polling
          or show popups in the background. */}
      <SurveysHubTabPanel
        key={activeTab.id}
        role="tabpanel"
        id={`surveys-hub-panel-${activeTab.id}`}
        aria-labelledby={`surveys-hub-tab-${activeTab.id}`}
      >
        {tabDetails[activeTab.id].content}
      </SurveysHubTabPanel>
    </SurveysHubWrapper>
  );
};

export default SurveysHubContainer;
